import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiTypes, DatasetDetails, ModeHalResource } from '@mode/shared/contract-common';
import {
  DataViewResponseTypes,
  ReportDomain,
  SwitchViewDomain,
  SwitchViewResponseTypes,
} from '@mode/shared/contract-common';
import { MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { ApiService, getEmbedded, getEmbeddedCollection } from '@mode/shared/util-halogen';
import { Observable, from } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  extractDatasetResultProps,
  fillUnrunQuerySwitchViewResource,
  normalizeSwitchView,
  switchViewResponseToDataView,
  toDataset,
} from '../data-mappers/data-view-data-mappers';
import {
  switchViewResourceHasDataset,
  toSwitchView,
  toSwitchViewRunState,
} from '../data-mappers/switch-view-data-mappers';
import { QueryResource } from '../queries/query-response.types';
import { ReportClientService } from '../reports/report-client.service';
import { ReportResource } from '../reports/report-response.types';
import { reportCache, switchViewAttrCache, switchViewCache } from '../resource-cache';
import { SwitchViewsRankMap } from 'libs/shared/contract-common/src/lib/switch/switch-view.types';
import { EmbeddedSwitchViewsResponse } from 'libs/shared/contract-common/src/lib/data-view/data-view-response.types';
import { isPublished, isTSPublishMetaData } from '../reports/report-response-predicates.types';

@Injectable({
  providedIn: 'root',
})
export class SwitchViewClientService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private reportClient: ReportClientService,
    private metaTags: MetaTagsService
  ) {}

  private getOrg() {
    const orgId = this.metaTags.getTag(MetaTags.OwnerUsername);

    if (!orgId) {
      throw new Error('orgId missing from meta tags! It should be in the owner-username tag');
    }

    return orgId;
  }

  public getSwitchView(switchViewToken: string): Observable<SwitchViewDomain.DataView> {
    return this.getSwitchViewWithResult(this.getOrg(), switchViewToken, null);
  }

  /**
   * Load single switchView with dataset result
   */
  public getSwitchViewWithResult(
    accountId: string,
    switchViewToken: string,
    reportRunId: string | null
  ): Observable<SwitchViewDomain.DataView> {
    // No cache since this is intended to be used after polling. Could be refactored though to be less wasteful.
    const baseUrl = `/api/${accountId}/switch_views/${switchViewToken}`;
    const url = reportRunId ? `${baseUrl}?report_run=${reportRunId}` : `${baseUrl}`;

    const datasetEmbedRequestKey = reportRunId ? 'data_run_for_run_in_context' : 'last_successful_data_run';

    // @TODO: (@anthonysimone) [GF] - Requesting these embeds without values as this is necessary for
    // the switch_view embeds from this endpoint currently.

    return from(
      this.apiService.getFromPath<SwitchViewResponseTypes.SwitchViewResource>(url, {
        includes: [
          { [datasetEmbedRequestKey]: ['creator', 'producer_run', 'result'] },
          'switch_attrs',
          'new_visualization',
          'new_switch_attr',
        ],
      })
    ).pipe(
      tap((resource) => {
        switchViewCache.set(switchViewToken, resource);
      }),
      switchMap((viewResource) =>
        from(
          this.apiService.getResources<SwitchViewResponseTypes.SwitchViewAttrResource>(viewResource, 'switch_attrs')
        ).pipe(
          tap((resources) => {
            resources.forEach((attr) => switchViewAttrCache.set(attr.token, attr));
          }),
          map(() => viewResource) // just return the original view since we only need to parse the attrs to cache
        )
      ),
      map((resource) => {
        // We should expect to only be calling this on switchViews with runs, but we need to validate here as a safety check
        // for now to prevent a potential syntax error as we need to type coerce below until the rest of the flows are moved
        // fully to the new patterns.
        if (!switchViewResourceHasDataset(resource)) {
          throw new Error(`View ${resource.token} has no dataset`);
        }
        // Temporarily coercing type to the response type, so downstream typings will be as expected.
        // This will need to be changed when the new api pathway and cache are used across the board.
        const switchViewResponse = resource as any as DataViewResponseTypes.SwitchViewWithRunsResponses;
        const normalizedSwitchView = normalizeSwitchView(switchViewResponse);
        return switchViewResponseToDataView(normalizedSwitchView);
      })
    );
  }

  /**
   * Deletes switch view resources from HAL resource cache by provided switch view tokens.
   * @param viewTokens
   */
  public deleteSwitchViewsFromCache(viewTokens: ReadonlyArray<string>) {
    viewTokens.forEach((viewToken) => switchViewCache.delete(viewToken));
  }

  /**
   * Get the run state for the given switchView.
   *
   * If reportRunToken is null, then use the last_run endpoint.
   *
   * Additionally, at this point in time, the expectation is that this endpoint
   * is only valid with local switch view tokens.
   *
   * @param accountId
   * @param reportToken
   * @param switchViewToken
   * @param reportRunToken
   */
  public getSwitchViewRunState(
    accountId: string,
    reportToken: string,
    switchViewToken: string,
    reportRunToken: string | null
  ) {
    const urlForRunToken = `/api/${accountId}/reports/${reportToken}/switch_views/${switchViewToken}/runs/${reportRunToken}`;
    const urlForLastRun = `/api/${accountId}/reports/${reportToken}/switch_views/${switchViewToken}/last_run`;

    const url = reportRunToken ? urlForRunToken : urlForLastRun;

    return from(this.apiService.getFromPath<SwitchViewResponseTypes.SwitchViewRunStateResource>(url)).pipe(
      map((switchViewRunStateResource) => toSwitchViewRunState(switchViewRunStateResource))
    );
  }

  public reloadFlamingoSignature(accountId: string, switchViewToken: string) {
    const url = `/api/${accountId}/switch_views/${switchViewToken}`;
    return from(this.apiService.getFromPath<SwitchViewResponseTypes.SwitchViewResource>(url)).pipe(
      map((resource) => resource.flamingo_signature)
    );
  }

  public refreshViewsInReport(accountId: string, reportToken: string) {
    return this.getDataView(
      reportToken,
      this.reportClient.getReportWithViewsWithoutCache(accountId, reportToken),
      true
    );
  }

  public getViewsInReport(accountId: string, reportToken: string) {
    return this.getDataView(reportToken, this.reportClient.getReportWithViews(accountId, reportToken), true);
  }

  public getViewsInDatasetReport(accountId: string, reportToken: string) {
    return this.getDataView(reportToken, this.reportClient.getDatasetReportWithViews(accountId, reportToken), false);
  }

  /**
   * @description Re-orders a SwitchView
   * This function makes an API request to re-order a single SwitchView based on the new rank provided
   *
   * @param reportToken the token of the report whose SwitchView is being re-ordered
   * @param switchViewToken the token of the SwitchView being re-ordered
   * @param prevSwitchViewToken the token of the SwitchView after which the re-ordered SwitchView will be positioned, null if first
   * @returns a map with all active SwitchViews for the report and their ranks
   */
  public reorderDatasets(
    accountId: string,
    reportToken: string,
    switchViewToken: string,
    prevSwitchViewToken: string | null
  ) {
    const rankUrl = `/api/${accountId}/reports/${reportToken}/switch_views/${switchViewToken}/reorder`;

    return this.http
      .patch<EmbeddedSwitchViewsResponse>(rankUrl, {
        preceding_view_token: prevSwitchViewToken,
      })
      .pipe(
        map((switchViewReorderedResponse) => {
          const reorderedSwitchViewResponses = switchViewReorderedResponse._embedded.switch_views;
          const switchViewRankMap: SwitchViewsRankMap = {};
          reorderedSwitchViewResponses.forEach((switchViewResponse) => {
            const { token, row_order } = switchViewResponse;
            switchViewRankMap[token] = row_order ?? null;
          });
          return switchViewRankMap;
        })
      );
  }

  /**
   * @description Create a SwitchView attribute
   * @param accountId meta[ownerUsername]
   * @param switchViewToken SwitchView token
   * @param attr
   */
  public createAttr(
    accountId: string,
    switchViewToken: string,
    attr: DataViewResponseTypes.SwitchViewAttr.RequestAttr
  ) {
    const request = `/api/${accountId}/switch_views/${switchViewToken}/switch_attrs`;
    const data: DataViewResponseTypes.SwitchViewAttr.Create = { attr };

    return this.http.post<DataViewResponseTypes.SwitchViewAttr.CreateResponse>(request, data).toPromise();
  }

  /**
   * @description Update a SwitchView attribute
   * @param accountId meta[ownerUsername]
   * @param switchViewToken SwitchView token
   * @param token SwitchView.Attribute token
   * @param attr
   */
  public updateAttr(
    accountId: string,
    switchViewToken: string,
    token: string,
    attr: DataViewResponseTypes.SwitchViewAttr.RequestAttr
  ) {
    const request = `/api/${accountId}/switch_views/${switchViewToken}/switch_attrs/${token}`;
    const data: DataViewResponseTypes.SwitchViewAttr.Update = { attr };

    return this.http.patch<DataViewResponseTypes.SwitchViewAttr.UpdateResponse>(request, data).toPromise();
  }

  /**
   * @description Delete a SwitchView attribute
   * @param accountId meta[ownerUsername]
   * @param token SwitchView.Attribute token
   * @param switchViewToken SwitchView token
   */
  public deleteAttr(accountId: string, token: string, switchViewToken: string) {
    const request = `/api/${accountId}/switch_views/${switchViewToken}/switch_attrs/${token}`;
    return this.http
      .delete(request)
      .pipe(map(() => {}))
      .toPromise();
  }

  /**
   * @description Remove a dataset
   * @param accountId meta[ownerUsername]
   * @param reportToken meta[reportToken]
   * @param switchViewToken SwitchView token
   */
  public removeDataset(accountId: string, reportToken: string, switchViewToken: string) {
    const url = `/api/${accountId}/reports/${reportToken}/switch_views/${switchViewToken}`;

    return this.http.delete<any>(url);
  }

  /**
   * TODO: POC NO Cache
   */
  public getDataset(accountId: string, reportToken: string) {
    const url = `/api/${accountId}/reports/${reportToken}`;

    const switchViewsInclude = {
      reports: ['reports'],
      creator: ['creator'],
      local_switch_views: {
        switch_views: ['switch_attrs', { last_successful_data_run: ['result'] }],
      },
    };

    const includes = [switchViewsInclude];

    return from(this.apiService.getFromPath<ReportResource>(url, { includes })).pipe(
      map((response) => {
        return toDataset(response);
      })
    );
  }

  public refreshImportedDataset(
    reportId: string,
    switchViewToken: string
  ): Observable<DataViewResponseTypes.RefreshedDatasetResponse> {
    const url = `/api/${this.getOrg()}/reports/${reportId}/editor/runs`;

    const params = {
      'embed[dataset_snapshots][data_run][creator]': '1',
      'embed[dataset_snapshots][run_result]': '1',
      'embed[dataset_snapshots][switch_view][switch_attrs]': '1',
    };

    const payload = {
      report: {
        switch_views: [
          {
            token: switchViewToken,
          },
        ],
      },
    };

    return this.http.post<any>(url, payload, { params }).pipe(
      map((response) => {
        const [snap] = response._embedded.dataset_snapshots;
        const shareLink = response._links.share.href;

        return {
          dataRefreshed: snap.data_refreshed,
          shareLink,
          datasetResult: {
            ...extractDatasetResultProps({
              queryRun: snap._embedded.data_run,
              runResult: snap._embedded.run_result,
            }),
            datasetToken: snap._embedded.switch_view.root_model_token,
            producerType: snap._embedded.producer_type ?? null,
            error: null,
            isFallbackData: false,
            switchViewToken,
          },
          switchView: toSwitchView(
            snap._embedded.switch_view,
            snap._embedded.switch_view?._embedded?.switch_attrs?._embedded?.switch_attrs ?? []
          ),
          mostRecentReportRunToken: response.token,
        };
      })
    );
  }

  public getDataView(reportToken: string, apiCall: Observable<ReportDomain.Report>, isReport: boolean) {
    let datasetDetails: DatasetDetails;
    return apiCall.pipe(
      map(() => reportCache.get(reportToken)), // need resources instead of the data-mapped type
      map((reportResource) => {
        if (reportResource == null) {
          return [];
        }

        const hasEditAccess = !!reportResource?._forms?.['edit'] ?? false;
        const reportResourceSpace = reportResource?._embedded?.['space'] as ModeHalResource;
        const reportResourceCreator = reportResource?._embedded?.['creator'] as ModeHalResource;

        const importedSwitchViews =
          getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewResource>(
            reportResource,
            'imported_switch_views',
            'switch_views'
          ) ?? [];

        const localSwitchViews =
          getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewResource>(
            reportResource,
            'local_switch_views',
            'switch_views'
          ) ?? [];

        const reportImportsCount =
          ((localSwitchViews[0]?._embedded as ModeHalResource)?.['query']?.['report_imports_count'] as number) ?? 0;

        datasetDetails = {
          name: reportResource?.['name'],
          description: reportResource?.['description'],
          editedAt: reportResource?.edited_at,
          collection: {
            name: reportResourceSpace?.['name'],
            token: reportResourceSpace?.['token'],
            href: (reportResourceSpace?._links['web'] as ApiTypes.HalLink)?.['href'],
          },
          creator: {
            username: reportResourceCreator?.['username'],
            token: reportResourceCreator?.['token'],
            name: reportResourceCreator?.['name'],
            email: reportResourceCreator?.['email'],
            avatar: reportResourceCreator?.['avatar'],
          },
          lastSuccessfullyRunAt: reportResource?.['last_successfully_run_at'],
          reportImportsCount,
          hasEditAccess,
        };

        if (isPublished(reportResource)) {
          datasetDetails.thoughtspot = {
            publishedAt: reportResource.thoughtspot_published_at,
            publishToTsSucceededAt: reportResource.publish_to_thoughtspot_succeeded_at,
            publisher: reportResource._embedded.thoughtspot_publisher.name,
            publisherUsername: reportResource._embedded.thoughtspot_publisher.username,
            publishUrl: reportResource.thoughtspot_publish_url,
            publicationState: reportResource.thoughtspot_publication_state,
            datasetThoughtspotPublicationToken: reportResource.dataset_thoughtspot_publication_token,
          };
        }

        if (isTSPublishMetaData(reportResource)) {
          datasetDetails.tsPublishMetaData = {
            schemaName: reportResource._embedded.ts_publish_metadata.schema_name,
            tableName: reportResource._embedded.ts_publish_metadata.table_name,
          };
        }

        // Filter the localSwitchViews collection out into items with runs and items without runs (ie - returned datasets)
        const localSwitchViewsWithRuns: SwitchViewResponseTypes.SwitchViewResource[] = [];
        const localSwitchViewsWithoutRuns: SwitchViewResponseTypes.SwitchViewResource[] = [];

        localSwitchViews.forEach((switchViewResource) => {
          if (switchViewResourceHasDataset(switchViewResource)) {
            localSwitchViewsWithRuns.push(switchViewResource);
          } else {
            localSwitchViewsWithoutRuns.push(switchViewResource);
          }
        });

        let switchViews: SwitchViewResponseTypes.SwitchViewResource[] =
          importedSwitchViews.concat(localSwitchViewsWithRuns);

        // If there are any localUnrunSwitchViews, find their queries, and fill their datasets with necessary details
        // so they can be mapped.
        if (localSwitchViewsWithoutRuns.length > 0) {
          localSwitchViewsWithoutRuns.forEach((switchViewResource) => {
            const query = getEmbedded<QueryResource>(switchViewResource, 'query');
            if (query) {
              switchViews.push(fillUnrunQuerySwitchViewResource(switchViewResource, query));
            }
          });
        }

        return switchViews;
      }),
      tap((resources) =>
        resources.forEach((view) => {
          switchViewCache.set(view.token, view);
          const attrs = getEmbeddedCollection<SwitchViewResponseTypes.SwitchViewAttrResource>(view, 'switch_attrs');
          attrs?.forEach((attr) => {
            switchViewAttrCache.set(attr.token, attr);
          });
        })
      ),
      // Warning: this function assumes switch_attrs have been embedded in getReportWithViews
      map((resources) =>
        resources.map((view) => {
          const switchViewResponse = view as any as DataViewResponseTypes.SwitchViewWithRunsResponses;
          const normalizedSwitchView = normalizeSwitchView(switchViewResponse);
          return switchViewResponseToDataView(normalizedSwitchView, !isReport ? datasetDetails : undefined);
        })
      )
    );
  }
}
